<template>
  <component
    class="card"
    :class="{ [size]: true }"
    :is="componentType"
    :to="toIfLink"
    alt=""
  >
    <div class="image-container">
      <img class="image" :src="backgroundImage" alt="" role="presentation"/>
      <video-overlay v-if="isVideo" :small="size === 'event-small'" />
    </div>
    <div class="details">
      <div class="details-container">
        <div class="icon-wrapper" v-if="iconUrl">
          <icon class="icon" :icon="iconUrl"/>
        </div>
        <p class="date" v-if="fromDate || toDate">
          <date-formatter :timeStamp="fromDate" :timezone="timezone" :type="timeFormat" />
          <span v-if="toDate"> -
            <date-formatter :timeStamp="toDate" :timezone="timezone" :type="timeFormat" />
          </span>
        </p>
        <h3 class="name">{{ name }}</h3>
        <p class="description" v-if="description">{{ description }}</p>
        <p class="category" v-if="category">{{ category }}</p>

        <event-status
          class="status"
          v-if="status"
          :status="status"
          :date="opensAt"
          :timezone="timezone"
          :guests="guests"
          :digital="digital"
        />
        <sh-button-deprecated class="label" primary v-if="hasLabel" :to="to" aria-label="Load more notes" role="button">
          {{ $t(label) }}
        </sh-button-deprecated>
      </div>
    </div>
  </component>
</template>

<script>
import Card from '~/components/global/card'

export default {
  name: 'HouseCard',
  mixins: [Card]
}
</script>

<style lang="scss" scoped>

$card-shadow: 0 24px 30px -20px rgba($deprecated-grey-xx-dark, .1), 0 10px 60px 0 rgba($deprecated-grey-xx-dark, .15);
$card-border-radius: 4px;

.card {
  position: relative;
  display: block;
  text-decoration: none;
  will-change: auto;
  cursor: pointer;
  @include deprecated-f-color-normal;
  @include transition-fast;

  .image-container {
    width: 100%;
    min-height: 160px;
    position: relative;
    @include transition-fast;
  }

  .image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    will-change: auto;
    @include lazy-load;
  }

  .details {
    display: flex;
    flex-direction: row;
    padding: $space-050 0 $space-060;
    width: 100%;
    position: relative;
    @include transition-fast;

    .details-container {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .icon-wrapper {
      align-self: flex-end;
      height: $space-070;
      width: $space-070;
      margin-bottom: -$space-070;
    }

    .icon {
      height: 100%;
      width: 100%;
    }

    .category {
      @include label-xx-sml;
      margin: 0;
    }

    .date {
      @include label-xx-sml;
      @include deprecated-f-color-dark;
      order: -1;
      margin: 0;
      margin-bottom: $d-tiny;
    }

    .name {
      margin: $d-xx-small $space-070 $space-050 0;
      padding-right: $d-tiny;
      @include title-med;
      @include deprecated-f-color-dark;
    }

    .house {
      @include label-xx-sml;
      margin: 0;

      &.array {
        width: 100%;
        padding: $space-060 0;
        border-top: $line-medium;
        border-bottom: $line-medium;
      }
    }

    .status {
      margin-top: $d-tiny;
    }

    .description {
      display: none;
      margin-top: $space-050;
      margin-bottom: $space-050;
      word-break: break-word;
      @include body-lrg;
    }

    .label {
      align-self: center;
      width: auto;
      display: inline-flex;
      justify-content: center;
      margin-top: $space-060;
      padding: $d-xx-small $space-060;
      @include label-xx-sml;
    }
  }

  &:after {
    @include transition-fast;
    left: 50%;
    width: 100%;
    transform: translate3d(-50%, 0, 0);
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    bottom: 0;
    right: 0;
    width: 100%;
    transform: translate3d(-50%, 0, 0);
    background: $deprecated-white;
    box-shadow: 0 0 0 rgba($deprecated-grey-xx-dark, 0), 0 0 2px rgba($deprecated-grey-xx-dark, .5);
    border-radius: $card-border-radius;
    opacity: 0;
    @include transition-fast;
    outline: 1px solid rgba($deprecated-brand-tertiary, 0);
    outline-offset: 4px;
    visibility: hidden;
  }

  &:focus {
    .image {
      border-radius: $card-border-radius $card-border-radius 0 0;
    }
    &:before,
    &:after {
      opacity: 1;
      visibility: visible;
    }
    &:active {
      &:after {
        opacity: 0;
        visibility: hidden;
      }
    }
  }
}

// Card Style Variations
//-------------------------------------------------------------------

// Featured Cards
//-------------------------------------------------------------------

.card.featured-hero {
  display: flex;
  flex-direction: column;
  cursor: auto;

  .image-container {
    @include ratio(4, 3);
    overflow: hidden;
    @include breakpoint(medium) {
      @include ratio(16, 9);
    }
    .image {
      transition-duration: $t*4;
    }
  }

  .details {
    background: $deprecated-white;
    padding-top: $d-med;
    padding-bottom: $d-med;
    align-self: center;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    @include shadow(flyingPink);

    @include breakpoint(medium) {
      max-width: 720px;
      width: 80%;
      margin: -$d-huge $d-med 0;
      padding: $d-large $d-med;
      border-radius: $card-border-radius;
    }
    @include breakpoint(large) {
      margin-left: auto;
      margin-right: auto;
      padding: $space-080;
    }
  }

  .icon-wrapper {
    align-self: center;
    margin-bottom: $space-060;
    height: $space-080;
    width: $space-080;
    order: -1;
  }

  .date {
    display: block;
  }

  .name {
    @include title-x-lrg;
    margin: $space-050 auto;
    padding: 0 $space-060;
    order: 0;
  }

  &:focus {
    &:after,
    &:before {
      display: none;
    }
  }
}

.card.featured-secondary {
  margin-top: 0;

  .image-container {
    @include ratio(2, 1.2);
    overflow: hidden;
    @include breakpoint(medium) {
      @include ratio(4, 3);
    }
  }

  .details {
    padding: $space-060 $space-060 $d-med $space-060;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  .icon-wrapper {
    align-self: center;
    margin-bottom: $d-tiny;
  }

  .date {
    order: 0;
  }

  .name {
    margin: $space-050 auto;
    padding: 0 $space-050;
  }

  &:focus {
    .image {
      left: 0;
      width: 100%;
    }
    &:after,
    &:before {
      width: 100%;
    }
  }
}


// Block Lined Card
//-------------------------------------------------------------------

.card.featured-block {
  cursor: auto;

  .image-container {
    display: none;
  }

  .details {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;

    .icon-wrapper {
      margin-bottom: $space-070;
      width: 100%;

      .icon {
        display: block;
        @include horizontal-lines;
      }
    }

    .details-container {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .date {
      @include deprecated-f-color-normal;
      @include label-xx-sml;
      margin-bottom: $space-080;
      order: 0;
    }

    .name {
      @include title-lrg;
      @include transition-fast;
      padding: 0 $d-large;
      max-width: $bp-med;
      margin-left: auto;
      margin-right: auto;
    }

    .description {
      display: block;
      padding: 0 $d-large;
      max-width: $bp-med;
      margin-left: auto;
      margin-right: auto;
    }

    .label {
      align-self: center;
    }

    .status {
      display: none;
    }
  }
}


// Card Sizs
//-------------------------------------------------------------------

.card.event-large {
  @include breakpoint(medium) {
    display: flex;
    flex-direction: column;
    height: 100%;
    .image-container {
      flex-grow: 1;
    }
    .details {
      flex-shrink: 1;
    }
  }
  .image-container {
    @include ratio(16, 9);
  }
}

.card.event-med {

  .image-container {
    @include ratio(2, 1.2);

    @include breakpoint(medium) {
      @include ratio(3, 2);
    }
  }
}

.card.event-small {
  .image-container {
    @include ratio(16, 9);
  }

  .details {
    .name {
      @include font('050');
    }
  }
}

.card.event-tiny {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: $d-tiny;
  padding-right: 0;
  width: calc(100% + #{$d-tiny});
  margin-left: -$d-tiny;

  .image-container {
    @include ratio(1, 1);
    height: 100%;
    width: 50%;
    min-height: 80px;
    max-width: 120px;
    overflow: hidden;
  }

  .details {
    margin-left: $d-tiny;
    padding: 0;
    flex-direction: column;
    justify-content: space-around;

    .icon-wrapper {
      display: none;
    }

    .name {
      @include body-med;
      margin: 0;
      margin-bottom: $d-tiny;
    }

    .date {
      margin-top: 0;
    }

    .status {
      margin-top: $d-tiny / 2;
    }
  }

  &:focus {
    transform: none;
    .image {
      width: 100%;
      left: 0;
      border-radius: 0;
    }
    &:after,
    &:before {
      width: 100%;
    }
  }
}


// Editorial Cards
//-------------------------------------------------------------------

$card-gutter: $d-xx-large;

.card {
  &.editorial-left,
  &.editorial-right,
  &.editorial-bleed {
    display: flex;
    flex-direction: column;
    cursor: auto;

    .image-container {
      @include ratio(3,2);
      overflow: hidden;
      @include breakpoint(medium) {
        @include ratio(4,3);
        max-height: 40vw;
        min-height: 420px;
        width: calc(100% - #{$card-gutter});
        &:before {
          display: none;
        }
      }
      @include breakpoint(large) {
        max-height: 100%;
        min-height: 560px;
      }
    }

    .image {
      transition-duration: $t*4;
    }

    &:after,
    &:focus:after {
      display: none;
    }

    &:focus {
      &:after,
      &:before {
        display: none;
      }
    }

    .details {
      flex-direction: row-reverse;
      justify-content: center;
      padding: $space-070;
      text-align: center;

      @include breakpoint(medium) {
        flex-direction: column;
        padding: $d-xx-small;
        text-align: left;
      }

      @include breakpoint(large) {
        padding: $space-070;
      }

      .icon-wrapper {
        align-self: center;
        height: $d-med;
        width: $d-med;
        margin-bottom: $d-xx-small;
        @include breakpoint(medium) {
          align-self: flex-start;
        }
        @include breakpoint(large) {
          height: $d-large;
          width: $d-large;
          margin: $space-060 0;
        }
      }

      .date {
        order: 0;
        @include deprecated-f-color-normal;
      }

      .name {
        margin: $space-050 0;
        @include title-lrg;
        padding: 0;
      }

      .label {
        align-self: center;
        @include breakpoint(medium) {
          align-self: flex-start;
        }      }

      .category {
        display: none;
        @include breakpoint(large) {
          display: block;
        }
      }

      .description {
        display: none;
        @include breakpoint(medium) {
          display: block;
        }
      }
    }
  }

  &.editorial-left {
    @include breakpoint(medium) {
      flex-direction: row-reverse;
    }

    .details {
      @include breakpoint(medium) {
        padding-right: $card-gutter;
      }
      @include breakpoint(large) {
        padding-left: 0;
      }
    }
  }

  &.editorial-right {
    @include breakpoint(medium) {
      flex-direction: row;
    }

    .details {
      @include breakpoint(medium) {
        padding-left: $card-gutter;
      }
      @include breakpoint(large) {
        padding-right: 0;
      }
    }
  }

  &.editorial-bleed {
    @include breakpoint(medium) {
      flex-direction: row-reverse;
    }

    .details {
      @include breakpoint(medium) {
        padding-right: $card-gutter;
        width: calc(100% - #{$card-gutter/2});
      }
      @include breakpoint(large) {
        padding-left: 0;
      }
    }
    .image-container {
      @include breakpoint(medium) {
        width: 100%;
      }
    }
  }

  &.editorial-large {
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: auto;

    .image-container {
      @include ratio(4,3);
      overflow: hidden;
      @include breakpoint(medium) {
        &:before {
          display: none;
        }
      }
      .image {
        transition-duration: $t*4;
        @include transition-slow;
      }
    }

    &:after,
    &:focus:after {
      display: none;
    }

    &:focus {
      &:after,
      &:before {
        display: none;
      }
    }

    .details {
      flex-direction: row-reverse;
      align-content: center;
      text-align: center;
      padding: $space-070;
      @include breakpoint(medium) {
        flex-direction: column;
        padding: $d-large;
      }

      .details-container {
        flex-grow: 1;
      }

      .icon-wrapper {
        margin: $space-060 0;
        height: $d-med;
        width: $d-med;
        align-self: center;

        @include breakpoint(medium) {
          margin-top: 0;
        }
      }

      .date {
        order: 0;
      }

      .name {
        margin: $space-050 auto;
      }
    }

    @include breakpoint(medium) {
      position: relative;
      min-height: 560px;
      padding-top: $d-xx-large * 2;
      padding-bottom: $d-xx-large * 2;

      .image-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      .details {
        flex-basis: fit-content;
        width: auto;
        max-width: calc(100% - #{$d-xx-large * 4});
        margin: auto;
        border-radius: 4px;
        background-color: rgba($deprecated-white, .95);
        background: $deprecated-white;
        @supports (backdrop-filter: blur()){
          background-color: rgba($deprecated-white, .8);
          backdrop-filter: blur(40px) brightness(150%);
        }
      }
      .details-container {
        padding-left: $d-med;
        padding-right: $d-med;
      }
    }
  }
}

.promotional {
  .card {
    .details .description {
      display: block;
    }
  }
}

// House Note Card
//-------------------------------------------------------------------

.house-note .card {

  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .details-container {
    .description {
      @include body-med;
      @include deprecated-f-color-dark;
    }
  }

  &.event-med,
  &.event-small,
  &.event-large {
    .details-container {
      .icon-wrapper {
        display: none;
      }
      .name {
        @include body-lrg;
        font-family: $f-family-sans;
        font-weight: $f-weight-medium;
        order: -1;
        margin: 0;
        margin-bottom: $space-050;
        padding-right: 0;
      }

      .date {
        display: none;
      }

      .description {
        display: block;
        margin-top: 0;
      }
    }
  }

  &.event-large {

    .details-container {
      margin-top: 16px;

      .name {
        font-size: $f-size-x-lrg;
        color: $color-slate-dark;
        margin-block-start: 0;
        margin-block-end: 0;
        margin-bottom: $space-050;
      }
      .description {
        font-size: $f-base;
      }
    }
  }
}


// Empty Card
//-------------------------------------------------------------------
.empty.card {
  .details {
    .name {
      @include title-x-lrg;
      @include breakpoint(medium) {
        @include title-hero;
      }
    }
    .description {
      display: block;
    }
  }

  .image-container {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    .image {
      background-size: contain;
    }
  }
}
</style>
